'use client';

import { useEffect, useRef, useState } from 'react';
import type { Masonry as VirtualizedMasonry } from 'react-virtualized';
import {
  type ColumnsPreset,
  calculateColumnWidth,
  createCache,
  createPositioner,
  MASONRY_CONSTANTS,
  type Positioner,
} from '@/app/utils/client';

type MasonryRef = VirtualizedMasonry;

/**
 * @param bigLayout 是否为大布局
 * @param scrollElementId 滚动元素的 id
 * @returns
 */
export const useMasonry = ({
  scrollElementId,
  columnsPreset = 'default',
}: {
  scrollElementId: string;
  columnsPreset?: ColumnsPreset;
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  // 修改定位器的 ref 类型
  const cellPositioner = useRef<Positioner | null>(null);

  // 保存 Masonry 实例的引用
  const masonryRef = useRef<MasonryRef | null>(null);

  const cache = useRef(createCache({ columnsPreset }));

  /**
   * 重置缓存
   * @param width 宽度
   */
  const onResize = ({ width }: { width: number }) => {
    // 重置所有缓存
    cache.current.clearAll();

    // 重新创建缓存实例
    const CellMeasurerCache = require('react-virtualized').CellMeasurerCache;
    cache.current = new CellMeasurerCache({
      defaultHeight: MASONRY_CONSTANTS.DEFAULT_CELL.HEIGHT,
      defaultWidth: calculateColumnWidth({ width, columnsPreset }),
      fixedWidth: true,
      keyMapper: (index: number) => index.toString(),
    });

    // 重新创建定位器
    cellPositioner.current = createPositioner({ width, cache: cache.current, columnsPreset });

    // 确保 Masonry 组件存在后再重新计算位置
    if (masonryRef.current) {
      masonryRef.current.recomputeCellPositions();
    }
  };

  // 组件挂载后获取 scrollElement
  useEffect(() => {
    const element = scrollElementId && document.getElementById(scrollElementId);
    if (element) {
      setScrollElement(element);
    }
    setIsMounted(true);
  }, [scrollElementId]);

  return {
    // 是否挂载
    isMounted,
    // 滚动元素
    scrollElement,
    // Masonry 实例
    masonryRef,
    // 缓存
    cache,
    // 定位器
    cellPositioner,
    // 重置缓存
    onResize,
  };
};
